import moment from 'moment';
import { PACKAGE_HAS_EOL, PACKAGE_LEVEL_V3 } from '@src/constants/package';
import {
  GLOBAL_VOICE_FEATURES,
  VIETNAM_VOICE_FEATURES,
  VOICE_LEVEL,
  VOICE_PROVIDER,
} from '@src/constants/voice';
import { EOL_LABELS } from '@src/constants/eol';

const VOICE_TIER_FEATURES = [
  GLOBAL_VOICE_FEATURES.BASIC,
  GLOBAL_VOICE_FEATURES.ADVANCED,
  GLOBAL_VOICE_FEATURES.PREMIUM,
  VIETNAM_VOICE_FEATURES.BASIC,
  VIETNAM_VOICE_FEATURES.ADVANCED,
  VIETNAM_VOICE_FEATURES.PREMIUM,
];

const PACKAGE_LEVEL_LABEL_KEY = {
  [PACKAGE_LEVEL_V3.STANDARD]: 'standard',
  [PACKAGE_LEVEL_V3.SPECIAL]: 'enterprise',
  [PACKAGE_LEVEL_V3.VIP]: 'vip',
  [PACKAGE_LEVEL_V3.CUSTOM]: 'custom',
};

const PACKAGE_LEVEL_UPGRADE_ORDER = [
  PACKAGE_LEVEL_V3.STANDARD,
  PACKAGE_LEVEL_V3.SPECIAL,
  PACKAGE_LEVEL_V3.VIP,
  PACKAGE_LEVEL_V3.CUSTOM,
];

const DEFAULT_UPGRADE_LABEL_KEY = PACKAGE_LEVEL_LABEL_KEY[PACKAGE_LEVEL_V3.VIP];

const extractVoiceName = ({ name = '', provider = '' }) => {
  if (provider === VOICE_PROVIDER.VBEE) return { voiceName: name };
  if (name.includes(' - ')) {
    const [voiceName, voiceType] = name.split(/-(?=[^-]*$)/);
    return { voiceName, voiceType };
  }

  return { voiceName: name };
};

const checkAdvancedGlobalVoice = (voice) => {
  if (!voice) return false;

  const isGlobalVoice =
    voice.provider &&
    ![VOICE_PROVIDER.VBEE, VOICE_PROVIDER.VBEE_VOICE_CLONING].includes(
      voice.provider,
    );

  return voice.level === VOICE_LEVEL.ADVANCED && isGlobalVoice;
};

// V1: Legacy EOL logic
const canUseEOLVoiceV1 = ({ voice, user }) => {
  const voiceEOLDate = voice?.eolDate;
  const userEOLDate = user?.studio?.oneTime?.eolDate;
  const oneTimePackageCode = user?.studio?.oneTime?.packageCode;
  const oneTimeRemainingCharacters = user?.remainingCharacters;

  if (
    !voiceEOLDate ||
    !userEOLDate ||
    !oneTimePackageCode ||
    !oneTimeRemainingCharacters
  )
    return false;

  const latestEOLDate = new Date(
    Math.max(new Date(voiceEOLDate).getTime(), new Date(userEOLDate).getTime()),
  );

  const isAfterEOL = moment().isAfter(latestEOLDate);
  if (isAfterEOL) return false;

  return PACKAGE_HAS_EOL.includes(oneTimePackageCode);
};

const canUseEOLVoiceV2 = ({ voice, user }) => {
  const voiceEOLDate = voice?.eolDate || null;
  const voiceLabels = voice?.labels || [];
  const userVoiceEol = user?.eol?.voices || {};

  const voiceLabelEOL = voiceLabels.find((label) =>
    Object.values(EOL_LABELS).includes(label),
  );

  // Response from API is converted to camel case, example: eol_202503 -> eol202503
  const formattedVoiceLabelEOL = voiceLabelEOL
    ? voiceLabelEOL.replace('_', '')
    : null;

  const userEOLDate = userVoiceEol?.[formattedVoiceLabelEOL]?.eolAt;
  if (!userEOLDate) return false;

  const latestEOLDate = new Date(
    Math.max(new Date(voiceEOLDate).getTime(), new Date(userEOLDate).getTime()),
  );

  const isAfterEOL = moment().isAfter(latestEOLDate);
  if (isAfterEOL) return false;

  return true;
};

const formatNameForEOL = (name = '', userVoiceEolEnabled = false) => {
  if (!userVoiceEolEnabled) return name;
  if (name.includes('-VIP')) return name.replace('-VIP', '');
  return name;
};

const checkPremiumVoice = (voiceLevel) => voiceLevel === VOICE_LEVEL.PREMIUM;

const getRequiredVoiceTierFeatures = (voices = []) => {
  const safeVoices = Array.isArray(voices) ? voices : [voices].filter(Boolean);
  const features = new Set();
  safeVoices.forEach((voice) => {
    const voiceFeatures = Array.isArray(voice?.features) ? voice.features : [];
    voiceFeatures.forEach((feature) => {
      if (VOICE_TIER_FEATURES.includes(feature)) features.add(feature);
    });
  });
  return Array.from(features);
};

/**
 * Find the translation key (vd: 'standard'|'enterprise'|'vip'|'custom') for
 * the lowest-level package that provides at least one voice-tier feature the
 * given voice(s) require but the current user is still missing.
 *
 * Returns 'vip' as a safe fallback when nothing matches.
 */
const getUpgradePackageLabelKey = ({
  voices,
  voiceFeatures,
  userFeatures = [],
  packages = [],
} = {}) => {
  const requiredFeatures = Array.isArray(voiceFeatures)
    ? voiceFeatures.filter((feature) => VOICE_TIER_FEATURES.includes(feature))
    : getRequiredVoiceTierFeatures(voices);
  const safeUserFeatures = Array.isArray(userFeatures) ? userFeatures : [];
  const missingFeatures = requiredFeatures.filter(
    (feature) => !safeUserFeatures.includes(feature),
  );

  if (!missingFeatures.length) return DEFAULT_UPGRADE_LABEL_KEY;

  const safePackages = Array.isArray(packages) ? packages : [];

  const matchedLevel = PACKAGE_LEVEL_UPGRADE_ORDER.find((level) =>
    safePackages.some((pkg) => {
      if (pkg?.level !== level) return false;
      // Top-up packages carry voice-tier features as credit-tier markers (e.g. a
      // SPECIAL top-up lists advanced-global-voice for its special credits), not
      // as an actual feature grant. Matching on them mislabels the upgrade tier,
      // so exclude them and match only real subscription plans.
      if (pkg?.isTopUp) return false;
      const pkgFeatures = Array.isArray(pkg?.features) ? pkg.features : [];
      return missingFeatures.some((feature) => pkgFeatures.includes(feature));
    }),
  );

  return PACKAGE_LEVEL_LABEL_KEY[matchedLevel] || DEFAULT_UPGRADE_LABEL_KEY;
};

export {
  extractVoiceName,
  checkAdvancedGlobalVoice,
  canUseEOLVoiceV1,
  canUseEOLVoiceV2,
  formatNameForEOL,
  checkPremiumVoice,
  getRequiredVoiceTierFeatures,
  getUpgradePackageLabelKey,
};
